// src/services/payment/bankUmsaetzeService.ts
//
// Dienst für die Klärungsliste der Zahlungseingänge im Zahlungsabgleich
// (XTAX GmbH & Co. KG, 05.11.2026). Alle Aufrufe laufen über die
// Netlify-Funktion bank-umsaetze; das Frontend hat keinen direkten Zugriff
// auf die Tabelle bank_umsaetze.

import { netlifyFetch } from '../../shared/services/netlifyFetch';

export interface OffenerZahlungseingang {
  id: string;
  buchungstag: string | null;
  valuta: string | null;
  betrag: number;
  verwendungszweck: string | null;
  auftraggeber_name: string | null;
  auftraggeber_iban: string | null;
  zusatzinfo: string | null;
}

async function rufeAuf(daten: Record<string, unknown>): Promise<Record<string, unknown> & { success: boolean; error?: string }> {
  try {
    const response = await netlifyFetch('/.netlify/functions/bank-umsaetze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(daten)
    });
    const ergebnis = await response.json();
    if (!response.ok || !ergebnis.success) {
      return { ...ergebnis, success: false, error: ergebnis.error || `Anfrage fehlgeschlagen (HTTP ${response.status})` };
    }
    return ergebnis;
  } catch (error) {
    console.error('[BankUmsaetze]', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unbekannter Fehler' };
  }
}

export const bankUmsaetzeService = {

  /** Zahlungseingänge, die keiner Rechnung zugeordnet sind. */
  ladeOffene: async (): Promise<{ success: boolean; error?: string; umsaetze: OffenerZahlungseingang[] }> => {
    const daten = await rufeAuf({ aktion: 'offene' });
    const liste = (daten.umsaetze as Array<OffenerZahlungseingang & { betrag: number | string }> | undefined) || [];
    return {
      success: daten.success,
      error: daten.error,
      umsaetze: liste.map(u => ({ ...u, betrag: Number(u.betrag) }))
    };
  },

  /** Ordnet einen Zahlungseingang einer offenen Rechnung zu und markiert sie als bezahlt. */
  ordneZu: async (umsatzId: string, invoiceId: string, bearbeitetVon: string): Promise<{ success: boolean; error?: string; rechnungsnummer?: string }> => {
    const daten = await rufeAuf({ aktion: 'zuordnen', umsatz_id: umsatzId, invoice_id: invoiceId, bearbeitet_von: bearbeitetVon });
    return { success: daten.success, error: daten.error, rechnungsnummer: daten.rechnungsnummer as string | undefined };
  },

  /** Markiert einen Zahlungseingang als bewusst keiner Rechnung zugeordnet. */
  ignoriere: async (umsatzId: string, bearbeitetVon: string): Promise<{ success: boolean; error?: string }> => {
    const daten = await rufeAuf({ aktion: 'ignorieren', umsatz_id: umsatzId, bearbeitet_von: bearbeitetVon });
    return { success: daten.success, error: daten.error };
  }
};
